import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Icon, LogoMark } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { startLineLogin } from '@/auth/line';
import { useCompleteProfile } from '@/hooks/queries';
import { invites } from '@/data/invites';
import { professions } from '@/data/professions';
import { roleDefs } from '@/data/roleDefs';
import { ROLE_HOME } from '@/lib/rbac';
import { AuthBlocked, type AuthBlock } from './AuthBlocked';
import type { Invite, Profession } from '@/types/domain';

const REAL = import.meta.env.VITE_DATA_SOURCE === 'supabase';
const TG_BLUE = '#28a7e7';
const TG_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? 'warmpaper_club_bot';
const TG_USER = { name: 'Siriporn C.', username: 'siriporn_c', phone: '+66 81 ••• 5678' };

type Step = 'login' | 'auth' | 'profile';

// MOCK-only Telegram simulation. Real auth uses the Login Widget + Edge Function.
async function verifyTelegramMock(): Promise<{ profileComplete: boolean }> {
  await new Promise((r) => setTimeout(r, 1100));
  return { profileComplete: false };
}

/**
 * First-run login + onboarding. Invite-based: redeeming an invite link creates
 * an ACTIVE account immediately — no approval step. In production the Telegram
 * Login Widget posts a signed payload to the verify Edge Function; locally the
 * flow is mocked. The `?invite=<token>` param is forwarded to the function for
 * redemption (and previewed only in mock mode, since invites are admin-only).
 */
export function Welcome() {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');
  const checkinTok = params.get('checkin');
  const checkinCourse = params.get('course');
  const checkin = checkinTok && checkinCourse ? `${checkinCourse}|${checkinTok}` : null;
  const [step, setStep] = useState<Step>('login');
  const [blocked, setBlocked] = useState<AuthBlock | null>(null);

  const invite = useMemo<Invite | null>(() => {
    if (REAL || !inviteToken) return null;
    return invites.find((i) => i.token.toLowerCase() === inviteToken.toLowerCase()) ?? null;
  }, [inviteToken]);

  // Real mode: follow the live session — done users go home, new users onboard.
  useEffect(() => {
    if (!REAL || loading) return;
    if (user && user.onboarded !== false) {
      navigate(checkinTok && checkinCourse ? `/learn/course/${checkinCourse}/attendance?checkin=${checkinTok}` : ROLE_HOME[user.role], { replace: true });
    } else if (user && user.onboarded === false) setStep('profile');
  }, [user, loading, navigate, checkinTok, checkinCourse]);

  const goHome = () => navigate(user ? ROLE_HOME[user.role] : '/learn', { replace: true });

  // Escape hatch for a half-registered (or wrong) account: drop the session
  // and return to the login screen so a different person can sign in.
  const handleSignOut = () => {
    void signOut();
    setBlocked(null);
    setStep('login');
  };

  if (blocked) {
    return (
      <Shell>
        <AuthBlocked kind={blocked} onRetry={() => { setBlocked(null); setStep('login'); }} />
      </Shell>
    );
  }

  return (
    <Shell>
      {step === 'login' && (
        <LoginCard invite={invite} inviteToken={inviteToken} checkin={checkin} onMockNext={() => setStep('auth')} />
      )}
      {step === 'auth' && !REAL && (
        <TelegramAuthMock
          onResult={(r) => (r.profileComplete ? goHome() : setStep('profile'))}
          onBack={() => setStep('login')}
        />
      )}
      {step === 'profile' && (
        <>
          <ProfileSetup invite={invite} onDone={goHome} />
          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)' }}>
            {user?.firstEn ? `Signed in as ${user.firstEn}. ` : ''}Not you?{' '}
            <button onClick={handleSignOut} style={{ all: 'unset', cursor: 'pointer', color: 'var(--sage-deep)', fontWeight: 600 }}>Sign out</button>
          </p>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>{children}</div>
    </div>
  );
}

function Brandmark({ size = 56 }: { size?: number }) {
  return <LogoMark size={size} shadow />;
}

function InviteContext({ invite }: { invite: Invite }) {
  const p = professions[invite.profession];
  return (
    <div className="card" style={{ padding: '14px 16px', background: 'var(--sage-tint)', borderColor: 'transparent', marginTop: 28, textAlign: 'left' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Icon name="checkCircle" size={16} style={{ color: 'var(--sage)' }} />
        <span className="eyebrow" style={{ color: 'var(--sage-deep)' }}>You've been invited</span>
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 6 }}>
        {roleDefs[invite.role].label.en} · {p.label.en}
      </div>
      <div className="th muted" style={{ fontSize: 12.5, marginTop: 1 }}>{invite.dept} / {invite.unit}</div>
    </div>
  );
}

function LoginCard({
  invite,
  inviteToken,
  checkin,
  onMockNext,
}: {
  invite: Invite | null;
  inviteToken: string | null;
  checkin: string | null;
  onMockNext: () => void;
}) {
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Brandmark size={64} />
      <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', margin: '24px 0 0' }}>Warm Paper ED</h1>
      <div className="th" style={{ fontSize: 14.5, color: 'var(--ink-2)', marginTop: 6 }}>ระบบเรียนรู้สำหรับบุคลากร</div>
      <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: '14px 0 0', maxWidth: 280 }}>
        BBT Emergency · Education Platform
      </p>

      {invite && <InviteContext invite={invite} />}

      <div style={{ width: '100%', marginTop: invite ? 18 : 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        {REAL ? (
          <button onClick={() => startLineLogin(inviteToken, checkin)} className="btn btn-primary btn-lg" style={{ padding: '14px 34px', fontWeight: 600 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.2C6.6 3.2 2.2 6.8 2.2 11.2c0 2.4 1.4 4.6 3.6 6-.2.7-.6 2.1-.7 2.5-.1.3.1.5.4.4.3-.1 2.9-1.9 3.9-2.6.8.1 1.6.2 2.4.2 5.4 0 9.8-3.6 9.8-8C21.8 6.8 17.4 3.2 12 3.2z" /></svg>
            Continue with LINE
          </button>
        ) : (
          <button onClick={onMockNext} className="btn btn-lg btn-block" style={{ background: TG_BLUE, color: '#fff', boxShadow: `0 12px 26px -12px ${TG_BLUE}`, padding: '16px 22px' }}>
            <Icon name="telegram" size={20} /> Continue with Telegram (mock)
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 26 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', fontSize: 12.5, color: 'var(--ink-3)' }}>
          <Icon name="shield" size={15} style={{ color: 'var(--sage)' }} />
          <span><strong style={{ color: 'var(--ink-2)' }}>Invite-only</strong> · เฉพาะผู้ได้รับเชิญ</span>
        </div>
        {!invite && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-4)', lineHeight: 1.5 }}>
            Joining requires an invite link from a department admin.
          </p>
        )}
      </div>
    </div>
  );
}

/* MOCK-only Telegram consent simulation. */
function TelegramAuthMock({ onResult, onBack }: { onResult: (r: { profileComplete: boolean }) => void; onBack: () => void }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    onResult(await verifyTelegramMock());
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, alignItems: 'center', marginBottom: 26 }}>
        <Brandmark size={50} />
        <div style={{ display: 'flex', gap: 3, color: 'var(--ink-4)' }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 5, height: 5, borderRadius: 99, background: 'currentColor' }} />)}
        </div>
        <div style={{ width: 50, height: 50, borderRadius: 14, background: TG_BLUE, display: 'grid', placeItems: 'center', color: '#fff', boxShadow: `0 10px 24px -10px ${TG_BLUE}` }}>
          <Icon name="telegram" size={26} />
        </div>
      </div>

      <div className="card" style={{ padding: 4 }}>
        <div style={{ padding: '16px 16px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Log in to Warm Paper ED</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center', marginTop: 4, fontSize: 12.5, color: TG_BLUE }}>
            <Icon name="telegram" size={13} /> @{TG_BOT}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '13px 16px', margin: '0 4px', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', flex: 'none', background: 'linear-gradient(135deg,#37b3f0,#1f8fd0)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 15 }}>SC</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600 }}>{TG_USER.name}</div>
            <div className="muted" style={{ fontSize: 12.5 }}>@{TG_USER.username}</div>
          </div>
          <Icon name="checkCircle" size={20} style={{ color: TG_BLUE }} />
        </div>
        <div style={{ padding: '12px 16px 6px' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>This bot will receive</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {['Your name & username', 'Your profile photo'].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink-2)' }}>
                <Icon name="check" size={14} style={{ color: 'var(--sage)' }} /> {t}
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: 8 }}>
          <button onClick={go} disabled={busy} className="btn btn-lg btn-block" style={{ background: TG_BLUE, color: '#fff' }}>
            {busy ? 'Authorizing…' : 'Authorize'}
          </button>
        </div>
      </div>
      <button onClick={onBack} className="btn-quiet btn-block" style={{ marginTop: 14, fontSize: 13.5, color: 'var(--ink-3)', border: 'none', background: 'transparent', cursor: 'pointer' }}>Cancel</button>
    </div>
  );
}

function ProfileSetup({ invite, onDone }: { invite: Invite | null; onDone: () => void }) {
  const { user, updateProfile } = useAuth();
  const complete = useCompleteProfile();

  // Profession/dept/unit are fixed by the invite (read-only). In real mode they
  // come from the redeemed profile; in mock mode from the previewed invite.
  const profession: Profession = (REAL ? user?.profession : invite?.profession) ?? 'NURSE';
  const def = professions[profession];
  const dept = (REAL ? user?.deptId : invite?.dept) ?? 'IPD';
  const unit = (REAL ? user?.unitId : invite?.unit) ?? 'ICU';

  const [prefixIdx, setPrefixIdx] = useState(0);
  const [suffix, setSuffix] = useState(def.suffixes[0] ?? '');
  const [thFirst, setThFirst] = useState('');
  const [thLast, setThLast] = useState('');
  const [enFirst, setEnFirst] = useState(REAL ? user?.firstEn ?? '' : 'Siriporn');
  const [enLast, setEnLast] = useState(REAL ? user?.lastEn ?? '' : 'Charoen');
  const [err, setErr] = useState<string | null>(null);

  const needsSuffix = def.suffixes.length > 0;
  const valid = thFirst && thLast && enFirst && enLast && (!needsSuffix || suffix);
  const pre = def.prefixes[prefixIdx];
  const titleTh = [pre.th, thFirst, thLast].filter(Boolean).join(' ');
  const titleEnBase = [pre.en, enFirst, enLast].filter(Boolean).join(' ');
  const titleEn = suffix ? `${titleEnBase}, ${suffix}` : titleEnBase;

  const submit = async () => {
    const prefix = def.prefixes[prefixIdx];
    setErr(null);
    try {
      if (REAL) {
        await complete.mutateAsync({
          firstEn: enFirst, lastEn: enLast, firstTh: thFirst, lastTh: thLast,
          prefix: prefix.th, suffix: suffix || undefined,
        });
      }
      updateProfile({
        firstEn: enFirst, lastEn: enLast, firstTh: thFirst, lastTh: thLast,
        profession, titlePrefix: prefix.th, titleSuffix: suffix || undefined,
        title: prefix.en || prefix.th, deptId: dept, unitId: unit,
        initials: `${enFirst[0] ?? ''}${enLast[0] ?? ''}`.toUpperCase(), onboarded: true,
      });
      onDone();
    } catch {
      setErr('Could not save your profile. Please try again.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {['Telegram', 'Profile'].map((l, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 4, borderRadius: 99, background: 'var(--sage)' }} />
            <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--sage-deep)', fontFamily: 'var(--mono)', letterSpacing: '.04em' }}>{l}</span>
          </div>
        ))}
      </div>

      <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.02em', margin: 0 }}>Complete your profile</h1>
      <div className="th" style={{ fontSize: 14, color: 'var(--ink-3)', marginTop: 4, marginBottom: 14 }}>กรอกข้อมูลเพื่อใช้บนใบรับรอง</div>

      <div style={{ display: 'flex', gap: 8, padding: '11px 13px', background: 'var(--ochre-soft)', borderRadius: 10, fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 20, lineHeight: 1.55 }}>
        <Icon name="award" size={16} style={{ flex: 'none', marginTop: 1, color: 'var(--ochre)' }} />
        <span className="th"><strong>กรุณากรอกชื่อ-นามสกุล สำหรับใช้บนประกาศนียบัตร</strong> · Enter your full name as it should appear on your certificate — use your official name.</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <Avatar initials={`${enFirst[0] ?? ''}${enLast[0] ?? ''}`.toUpperCase() || 'WP'} size={84} tone="sage" />
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>From your Telegram photo · initials fallback</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ReadonlyChip icon="user" label={def.label.en} />
          <ReadonlyChip icon="bldg" label={dept} />
          <ReadonlyChip icon="pin" label={unit} />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: -10 }}>Profession, department and unit are set by your invite.</div>

        <div style={{ display: 'flex', gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Title · คำนำหน้า</span>
            <select className="select th" value={prefixIdx} onChange={(e) => setPrefixIdx(+e.target.value)}>
              {def.prefixes.map((p, i) => <option key={i} value={i}>{p.th}{p.en ? ` (${p.en})` : ''}</option>)}
            </select>
          </label>
          {needsSuffix && (
            <label className="field" style={{ flex: 1 }}>
              <span className="label">Designation</span>
              <select className="select" value={suffix} onChange={(e) => setSuffix(e.target.value)}>
                {def.suffixes.map((sx) => <option key={sx} value={sx}>{sx}</option>)}
              </select>
            </label>
          )}
        </div>

        <label className="field">
          <span className="label th">ชื่อ–นามสกุล (ภาษาไทย)</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="input th" value={thFirst} onChange={(e) => setThFirst(e.target.value)} placeholder="ชื่อ" />
            <input className="input th" value={thLast} onChange={(e) => setThLast(e.target.value)} placeholder="นามสกุล" />
          </div>
        </label>

        <div className="field">
          <span className="label">Legal name (English)</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="input" value={enFirst} onChange={(e) => setEnFirst(e.target.value)} placeholder="First name" />
            <input className="input" value={enLast} onChange={(e) => setEnLast(e.target.value)} placeholder="Last name" />
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Appears as <strong className="th">{titleTh}</strong> (TH) · <strong>{titleEn}</strong> (EN) on your certificate.
          </span>
        </div>

        {err && <div style={{ fontSize: 12.5, color: 'var(--clay)' }}>{err}</div>}

        <button onClick={submit} disabled={!valid || complete.isPending} className="btn btn-primary btn-lg btn-block">
          {complete.isPending ? 'Saving…' : <>Enter Warm Paper ED <Icon name="arrowR" size={17} /></>}
        </button>
        <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>Your account is active — no approval needed.</p>
      </div>
    </div>
  );
}

function ReadonlyChip({ icon, label }: { icon: 'user' | 'bldg' | 'pin'; label: string }) {
  return (
    <span style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, padding: '7px 12px', background: 'var(--card-2)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink-2)' }}>
      <Icon name={icon} size={14} style={{ color: 'var(--ink-3)' }} /> {label}
    </span>
  );
}
